import React, { useCallback, useEffect } from 'react';
import { Box, Inscription } from '@common-fe/common-fe';

import { ClaimSelectPlace } from '@/modules/employer/components/Lockbox/lockbox.types';
import { LoadNextItemsButton, MatchingListEmptyState } from '@/modules/employer/components/Lockbox/LockboxDetails/components/LockboxInfo/LockboxMatchingList';
import useClaimsCardsList from '@/modules/employer/components/Lockbox/LockboxDetails/hooks/useClaimsCardsList';
import useLockboxDetailsStore from '@/modules/employer/components/Lockbox/LockboxDetails/stores/useLockboxDetails.store';
import usePagination from '@/utils/hooks/usePagination';

import ClaimsList from './components/ClaimsList';
import useExtendClaimsWithMatchingData from './hooks/useExtendClaimsWithMatchingData';
import useModifyClaimRepayments from './hooks/useModifyClaimRepayments';

const SuggestedLockboxClaims = () => {
  const { editableLockbox, setEditableLockbox, isEditMode } = useLockboxDetailsStore();
  const { toggleClaim } = useModifyClaimRepayments();
  const suggestedLockboxClaims = editableLockbox?.suggestedClaims;
  const { claimsWithMatchingData, isLoaded } = useExtendClaimsWithMatchingData({ claims: suggestedLockboxClaims });
  const { fields } = useClaimsCardsList({ lockboxClaims: suggestedLockboxClaims });
  const { slicedItems, hasNextItems, handleNext } = usePagination({ items: fields, initialSize: 4, stepSize: 4 });

  const handleToggleClaim = useCallback((claimId?: string) => {
    const claimData = suggestedLockboxClaims?.find((claim) => claim?.claimId && claim?.claimId === claimId);
    toggleClaim(claimId, ClaimSelectPlace.SUGGESTED_CLAIMS, claimData);
  }, [toggleClaim, suggestedLockboxClaims]);

  useEffect(() => {
    if (isLoaded) {
      setEditableLockbox({ suggestedClaims: claimsWithMatchingData });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoaded]);

  return (
    <Box data-testId="suggested-lockbox-claims_wrapper">
      <Inscription color="textSecondary" margin={{ bottom: 'spacing8' }}>Suggested:</Inscription>

      <ClaimsList
        fields={slicedItems}
        isEditMode={isEditMode}
        selectedClaims={editableLockbox?.claimRepayments}
        onSelectClaim={handleToggleClaim}
        emptyState={(
          <Box margin={{ top: 'spacing8' }}>
            <MatchingListEmptyState title="No Suggestions" testId="suggested-lockbox-claims" />
          </Box>
        )}
        selectPlace={ClaimSelectPlace.SUGGESTED_CLAIMS}
        showWarnings
      />

      {!!slicedItems?.length && hasNextItems && (
        <Box margin={{ top: 'spacing8' }} align="center">
          <LoadNextItemsButton
            testId="suggested-lockbox-claims"
            hasNextItems={hasNextItems}
            onClick={handleNext}
          />
        </Box>
      )}
    </Box>
  );
};

export default SuggestedLockboxClaims;
